import { useCurrency } from 'src/hooks/v3/useCurrency';
import { usePool } from 'src/hooks/v3/usePool';
import { usePositionFees } from 'src/hooks/v3/usePositionFees';
import { PositionFromTokenId } from 'src/hooks/v3/usePositions';
import {
  Currency,
  CurrencyAmount,
  Percent,
  Position,
  computePoolAddress,
  unwrappedToken
} from '@cryptoalgebra/kim-sdk';
import { useCallback, useMemo } from 'react';
import { create } from 'zustand';
import { useAddress } from '@thirdweb-dev/react';
import { Address } from '@thirdweb-dev/sdk';

interface BurnState {
  readonly percent: number;
  readonly burnPosition: boolean;
  actions: {
    selectPercent: (percent: number) => void;
    toggleBurnPosition: (val: boolean) => void;
  };
}

export const useBurnState = create<BurnState>(set => ({
  percent: 0,
  burnPosition: false,
  actions: {
    selectPercent: (percent: number) => set({ percent }),
    toggleBurnPosition: (val: boolean) => set({ burnPosition: val })
  }
}));

export function useDerivedBurnInfo(
  position?: PositionFromTokenId,
  asWETH = false
): {
  position?: Position;
  liquidityPercentage?: Percent;
  liquidityValue0?: CurrencyAmount<Currency>;
  liquidityValue1?: CurrencyAmount<Currency>;
  feeValue0?: CurrencyAmount<Currency>;
  feeValue1?: CurrencyAmount<Currency>;
  outOfRange: boolean;
  error?: string;
} {
  const account = useAddress();

  const { percent } = useBurnState();

  const currency0 = useCurrency(position?.token0);
  const currency1 = useCurrency(position?.token1);

  const poolId =
    currency0 &&
    currency1 &&
    (computePoolAddress({
      tokenA: currency0.wrapped,
      tokenB: currency1.wrapped
    }) as Address);

  const [, pool] = usePool(poolId);

  const positionSDK = useMemo(
    () =>
      pool &&
      position?.liquidity &&
      typeof position?.tickLower === 'number' &&
      typeof position?.tickUpper === 'number'
        ? new Position({
            pool,
            liquidity: position.liquidity.toString(),
            tickLower: position.tickLower,
            tickUpper: position.tickUpper
          })
        : undefined,
    [pool, position]
  );

  const { liquidityPercentage, liquidityValue0, liquidityValue1 } =
    useMemo(() => {
      const liquidityPercentage = new Percent(percent, 100);

      const discountedAmount0 = positionSDK
        ? liquidityPercentage.multiply(positionSDK.amount0.quotient).quotient
        : undefined;
      const discountedAmount1 = positionSDK
        ? liquidityPercentage.multiply(positionSDK.amount1.quotient).quotient
        : undefined;

      const liquidityValue0 =
        currency0 && discountedAmount0
          ? CurrencyAmount.fromRawAmount(
              asWETH ? currency0 : unwrappedToken(currency0),
              discountedAmount0
            )
          : undefined;
      const liquidityValue1 =
        currency1 && discountedAmount1
          ? CurrencyAmount.fromRawAmount(
              asWETH ? currency1 : unwrappedToken(currency1),
              discountedAmount1
            )
          : undefined;

      return {
        liquidityPercentage,
        liquidityValue0,
        liquidityValue1
      };
    }, [percent, positionSDK, currency0, asWETH, currency1]);

  const { amount0: feeValue0, amount1: feeValue1 } = usePositionFees(
    pool ?? undefined,
    Number(position?.tokenId),
    asWETH
  );

  // const feeValue0 = undefined; //CurrencyAmount.fromRawAmount(currency0!, 0);
  // const feeValue1 = undefined; //CurrencyAmount.fromRawAmount(currency0!, 0);

  const outOfRange =
    pool && position
      ? pool.tickCurrent < position.tickLower ||
        pool.tickCurrent > position.tickUpper
      : false;

  let error: string | undefined;

  if (!account) {
    error = `Connect Wallet`;
  }

  if (percent === 0) {
    error = error ?? `Enter a percent`;
  }

  return useMemo(
    () => ({
      position: positionSDK,
      liquidityPercentage,
      liquidityValue0,
      liquidityValue1,
      feeValue0,
      feeValue1,
      outOfRange,
      error
    }),
    [
      positionSDK,
      liquidityPercentage,
      liquidityValue0,
      liquidityValue1,
      feeValue0,
      feeValue1,
      outOfRange,
      error
    ]
  );
}

export function useBurnActionHandlers(): {
  onPercentSelect: (percent: number) => void;
  onToggleBurnPosition: (val: boolean) => void;
} {
  const {
    actions: { selectPercent, toggleBurnPosition }
  } = useBurnState();

  const onPercentSelect = useCallback(
    (percent: number) => {
      selectPercent(percent);
    },
    [selectPercent]
  );

  const onToggleBurnPosition = useCallback(
    (val: boolean) => {
      toggleBurnPosition(val);
    },
    [toggleBurnPosition]
  );

  return {
    onPercentSelect,
    onToggleBurnPosition
  };
}
